import _ from 'lodash'
import { BaseDataItem, Component, LayoutDataItems, SingleLayoutData } from '@wix/thunderbolt-becky-types'
import { REDUCE_FUNCTIONS, toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import { createCssCompNode } from '../cssCompNode'
import { FeatureRefs } from '../cssFeatures.types'
import { envRefs } from '@wix/thunderbolt-catharsis'
import { isRefArray } from '../../utils/variantsUtils'

const isSingleLayoutData = (item: BaseDataItem): item is SingleLayoutData => item.type === 'SingleLayoutData'

const isMeshLayoutItem = (item: LayoutDataItems): boolean => {
	return item.type === 'MeshItemLayout' || item.type === 'MeshContainerLayout'
}

const ITEM_LAYOUTS = ['componentLayout', 'containerLayout', 'itemLayout'] as const
const getResponsiveItemLayouts = (item: LayoutDataItems): Array<LayoutDataItems> => {
	if (!isSingleLayoutData(item)) {
		return [item]
	}

	const res = []
	const { spx } = item

	for (const layout of ITEM_LAYOUTS) {
		const layoutItem = item[layout]
		if (layoutItem?.type && !isMeshLayoutItem(layoutItem)) {
			res.push({ ...layoutItem, spx })
		}
	}
	return res
}

export const compLayouts = createCssCompNode('responsiveLayout', 'compLayouts', {
	getDependencies: (component: Component, refs: FeatureRefs<'responsiveLayout'>) =>
		component.layoutQuery ? refs.layoutQuery(component.layoutQuery) : undefined,
	toViewItem: (__, refArray) =>
		isRefArray(refArray)
			? toBreakpointVariantsMap(refArray.values, getResponsiveItemLayouts, REDUCE_FUNCTIONS.concat)
			: null,
})

export const editorCompLayouts = createCssCompNode('responsiveLayout', 'compLayouts', {
	getDependencies: (component: Component, refs: FeatureRefs<'responsiveLayout'>) =>
		component.layoutQuery
			? {
					refArray: refs.layoutQuery(component.layoutQuery),
					activeVariant: envRefs.activeVariant,
					activeBreakpoints: envRefs.activeBreakpoints,
					computeOnlyActiveVariant: envRefs.experiment('specs.thunderbolt.compute_css_by_active_variant'),
					computeOnlyActiveBps: envRefs.experiment('specs.thunderbolt.compute_css_by_active_breakpoints'),
			  }
			: undefined,
	toViewItem: (__, deps) => {
		const { refArray, activeVariant, activeBreakpoints, computeOnlyActiveVariant, computeOnlyActiveBps } =
			deps || {}

		if (!isRefArray(refArray)) {
			return null
		}
		const breakpointVariantsMap = toBreakpointVariantsMap(
			refArray.values,
			getResponsiveItemLayouts,
			REDUCE_FUNCTIONS.concat
		)

		const activeBreakpointsIds = _.keys(activeBreakpoints)

		const shouldFilterActiveVariant = computeOnlyActiveVariant && activeVariant
		const shouldFilterActiveBps = computeOnlyActiveBps && activeBreakpointsIds.length > 0

		if (shouldFilterActiveVariant) {
			const defaultVariants = _.pick(breakpointVariantsMap.default, ['', activeVariant])
			return { default: defaultVariants }
		}

		if (shouldFilterActiveBps) {
			return _.pick(breakpointVariantsMap, ['default', ...activeBreakpointsIds])
		}

		return breakpointVariantsMap
	},
})
